import React, { useState } from "react";
import { FileText, Download } from "react-feather";
import { exportListToPdf, exportListToExcel } from "./listExportUtils";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useGetCompanyInfoQuery } from "../../../settings/settingsApi";

// Drop-in "Export PDF" / "Export Excel" button pair for any list page.
// Thin UI wrapper over listExportUtils - a page just hands over its current
// filtered rows plus a plain column-accessor list, same props for both
// buttons. See listExportUtils.js for the column/totals shape.
// `pdfColumns` (optional): a separate column set for the PDF export only,
// when it needs to drop something Excel keeps (e.g. Stock Movement Report
// dropping Branch from PDF - a printed page is usually one branch's own
// paperwork already, Excel is more often the "work with the raw data"
// format). Falls back to `columns` when omitted, so every existing caller
// is unaffected.
// `groupOrder` / `orientation` (optional): see listExportUtils - a fixed
// group order instead of alphabetical, and a landscape PDF for wide reports.
const ListExportToolbar = ({
  title, subtitleLines, branchCode, columns, pdfColumns, rows, totals, fileName, groupBy, groupTotals, groupOrder, orientation,
}) => {
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // The branch's own company name on the letterhead (2026-09-10). Until now
  // every list export printed "TEXAS ALARMS (K) LTD" whatever the branch - so a
  // SYSTEMS stock balance came out under the wrong company. Same source the LPO
  // and quotation prints already use (/settings/company-info?branchId=, e.g.
  // SYSTEMS -> "TEXAS SYSTEMS LTD"); done here once for all 14 list exports.
  // An "All branches" export (no branchCode) keeps the group name.
  const { data: branches = [] } = useGetBranchesQuery();
  const branchId = branchCode ? branches.find((b) => b.branchCode === branchCode)?.branchId : undefined;
  const { data: companyInfo } = useGetCompanyInfoQuery(branchId, { skip: !branchId });
  // undefined (not "") so listExportUtils' own default still applies when a
  // branch has no Company.Name configured (TEXASTRACK today).
  const companyName = (branchId && companyInfo?.["Company.Name"]) || undefined;

  const handlePdf = async () => {
    setIsExportingPdf(true);
    try {
      await exportListToPdf({
        title, subtitleLines, branchCode, companyName, columns: pdfColumns || columns, rows, totals, fileName, groupBy, groupTotals,
        groupOrder, orientation,
      });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleExcel = () => {
    exportListToExcel({ title, subtitleLines, branchCode, companyName, columns, rows, totals, fileName, groupBy, groupTotals, groupOrder });
  };

  // Texas brand maroon (same #6E1423 as the Sales quotation PDF's letterhead
  // color, quotation_pdf_builder.dart's _brandMaroon) - deliberately bolder/
  // taller than the plain outline buttons around it so Export doesn't get
  // lost among the filter controls.
  const buttonStyle = {
    backgroundColor: "#6E1423",
    borderColor: "#6E1423",
    color: "#fff",
    padding: "0.5rem 1rem",
    fontWeight: 500,
  };

  return (
    <div className="d-flex gap-2 flex-shrink-0">
      <button type="button" className="btn" style={buttonStyle} onClick={handlePdf} disabled={isExportingPdf}>
        <FileText size={16} className="me-1" />
        {isExportingPdf ? "Preparing..." : "Export PDF"}
      </button>
      <button type="button" className="btn" style={buttonStyle} onClick={handleExcel}>
        <Download size={16} className="me-1" />
        Export Excel
      </button>
    </div>
  );
};

export default ListExportToolbar;
